import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScreensaverSettings = ({ organization, onUpdate }) => {
  const [enabled, setEnabled] = useState(false);
  const [startTime, setStartTime] = useState('22:00');
  const [endTime, setEndTime] = useState('07:00');
  const [clockType, setClockType] = useState('digital');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [overlayOpacity, setOverlayOpacity] = useState(0.5);
  const [backgroundColor, setBackgroundColor] = useState('#000000');
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [screens, setScreens] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchScreens();
  }, []);

  useEffect(() => {
    if (organization) {
      setEnabled(organization.screensaver_enabled || false);
      setStartTime(organization.screensaver_start_time || '22:00');
      setEndTime(organization.screensaver_end_time || '07:00');
      setClockType(organization.screensaver_clock_type || 'digital');
      setBackgroundImage(organization.screensaver_background_image || null);
      setOverlayOpacity(organization.screensaver_overlay_opacity || 0.5);
      setBackgroundColor(organization.screensaver_background_color || '#000000');
      setSelectedScreens(organization.screensaver_screen_ids || []);
    }
  }, [organization]);

  const fetchScreens = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScreens(response.data);
    } catch (error) {
      console.error('Error fetching screens:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      // Upload image if selected
      let imageUrl = backgroundImage;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const uploadResponse = await axios.post(`${API}/screensaver/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        imageUrl = uploadResponse.data.image_url;
      }

      // Update organization settings
      const updateData = {
        screensaver_enabled: enabled,
        screensaver_start_time: startTime,
        screensaver_end_time: endTime,
        screensaver_clock_type: clockType,
        screensaver_background_image: imageUrl,
        screensaver_overlay_opacity: overlayOpacity,
        screensaver_background_color: backgroundColor,
        screensaver_screen_ids: selectedScreens
      };

      console.log('Saving screensaver settings:', updateData);

      await axios.patch(`${API}/organizations/me`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Skjermsparer-innstillinger lagret');
      if (onUpdate) onUpdate();
      setSelectedFile(null);
      setFilePreview(null);
    } catch (error) {
      console.error('Error saving screensaver settings:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke lagre innstillinger';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Default screensaver background image URL
  const DEFAULT_SCREENSAVER_IMAGE = 'https://customer-assets.emergentagent.com/job_59b77813-4cdc-4397-b7fb-26083f238e7e/artifacts/luxaj3x2_9.jpg';

  const useDefaultBackground = () => {
    setBackgroundImage(DEFAULT_SCREENSAVER_IMAGE);
    setFilePreview(DEFAULT_SCREENSAVER_IMAGE);
    setSelectedFile(null);
    toast.success('Standard bakgrunnsbilde valgt');
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
      borderRadius: '12px',
      padding: '2rem',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Clock size={20} color="#60a5fa" />
        <h3 style={{ color: 'white', fontSize: '0.9rem', margin: 0 }}>
          Skjermsparer-innstillinger
        </h3>
      </div>

      <p style={{ color: '#9ca3af', marginBottom: '2rem', fontSize: '0.875rem' }}>
        Konfigurer skjermsparer med klokke for å forhindre innbrenning på nattestid
      </p>

      {/* Enable Toggle */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Aktiver skjermsparer
        </label>
      </div>

      {enabled && (
        <>
          {/* Time Range */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  Start-tid
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  Slutt-tid
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.6)', 
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}>
              💡 Tider bruker din lokale tidssone (Norge: GMT+1)
            </div>
          </div>

          {/* Clock Type */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Klokke-type
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{ 
                flex: 1,
                padding: '0.75rem',
                background: clockType === 'digital' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${clockType === 'digital' ? '#60a5fa' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                color: 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  value="digital"
                  checked={clockType === 'digital'}
                  onChange={(e) => setClockType(e.target.value)}
                  style={{ display: 'none' }}
                />
                Digital
              </label>
              <label style={{ 
                flex: 1,
                padding: '0.75rem',
                background: clockType === 'analog' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${clockType === 'analog' ? '#60a5fa' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                color: 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  value="analog"
                  checked={clockType === 'analog'}
                  onChange={(e) => setClockType(e.target.value)}
                  style={{ display: 'none' }}
                />
                Analog
              </label>
            </div>
          </div>

          {/* Screen Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Vis på skjermer (tom = alle skjermer)
            </label>
            <div style={{ 
              maxHeight: '120px', 
              overflow: 'auto',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              padding: '0.75rem'
            }}>
              {screens.map(screen => {
                const isLandscape = screen.orientation === 'landscape';
                return (
                  <label key={screen.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    color: 'white',
                    marginBottom: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedScreens.includes(screen.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedScreens([...selectedScreens, screen.id]);
                        } else {
                          setSelectedScreens(selectedScreens.filter(id => id !== screen.id));
                        }
                      }}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                      {isLandscape ? (
                        <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                      ) : (
                        <rect x="6" y="2" width="12" height="20" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                      )}
                    </svg>
                    <span>{screen.screen_name || screen.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Background Image */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Bakgrunnsbilde
            </label>
            
            {/* Default Image Button */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => useDefaultBackground()}
                style={{
                  padding: '0.75rem 1rem',
                  background: backgroundImage === DEFAULT_SCREENSAVER_IMAGE ? 'rgba(80, 227, 194, 0.3)' : 'rgba(59, 130, 246, 0.2)',
                  color: backgroundImage === DEFAULT_SCREENSAVER_IMAGE ? '#50E3C2' : '#60a5fa',
                  border: `1px solid ${backgroundImage === DEFAULT_SCREENSAVER_IMAGE ? '#50E3C2' : 'rgba(59, 130, 246, 0.3)'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: backgroundImage === DEFAULT_SCREENSAVER_IMAGE ? '600' : '400',
                  width: '100%'
                }}
              >
                Bruk standard bakgrunnsbilde
              </button>
            </div>

            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer'
              }}
            />
            
            {filePreview && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <img
                  src={filePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '6px'
                  }}
                />
              </div>
            )}
            {!filePreview && backgroundImage && !['AUTO_DETECT', 'DEFAULT_LANDSCAPE', 'DEFAULT_PORTRAIT'].includes(backgroundImage) && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <img
                  src={backgroundImage.startsWith('http') ? backgroundImage : `${BACKEND_URL}${backgroundImage}`}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '6px'
                  }}
                />
              </div>
            )}
          </div>

          {/* Overlay Opacity */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Mørkt overlegg (opasitet): {Math.round(overlayOpacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '0.75rem', 
              color: '#9ca3af',
              marginTop: '0.25rem'
            }}>
              <span>0% (ingen overlegg)</span>
              <span>100% (helt mørkt)</span>
            </div>
          </div>

          {/* Background Color */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Overleggsfarge
            </label>
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              style={{
                width: '100px',
                height: '40px',
                padding: '0.25rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Overlay Opacity */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Overlegg Opasitet: {Math.round(overlayOpacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                background: `linear-gradient(to right, transparent 0%, ${backgroundColor} 100%)`,
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.75rem', opacity: 0.6, color: 'white' }}>
              <span>Gjennomsiktig</span>
              <span>Ugjennomsiktig</span>
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          type="button"
          onClick={() => {
            // Open preview in new window with test parameters
            const previewUrl = `/screensaver-preview?clockType=${clockType}&bgImage=${encodeURIComponent(backgroundImage || 'AUTO_DETECT')}&opacity=${overlayOpacity}&bgColor=${encodeURIComponent(backgroundColor)}`;
            window.open(previewUrl, '_blank', 'width=1920,height=1080');
          }}
          style={{
            flex: 1,
            padding: '0.75rem 1.5rem',
            background: 'rgba(80, 227, 194, 0.2)',
            color: '#50E3C2',
            border: '1px solid rgba(80, 227, 194, 0.3)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem'
          }}
        >
          👁️ Forhåndsvisning
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          style={{
            flex: 1,
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Lagrer...' : 'Lagre innstillinger'}
        </button>
      </div>
    </div>
  );
};

export default ScreensaverSettings;
